import dotPropImmutable from 'dot-prop-immutable';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useDispatch } from 'react-redux';
import {
  Account,
  AccountAPI,
  AccountInitialData,
  AccountSignUpPayload,
  AccountSignUpResponse,
  CurrencyList,
  IsDomainExistsPayload,
  IsDomainExistsResponse,
  NotificationData,
  UpdateAccountCurrencyPayload,
  UpdateAccountTimezonePayload,
} from 'api/account';
import { Company } from 'api/models';
import { GENERAL_API_STALE } from 'config/query-config';
import { RoutePaths } from 'constant';
import { set } from 'data/account';
import { success } from 'data/current-user-profile';
import { personalTasksSlice } from 'data/personal-tasks/slice';
import { putAll as putAllCompanies } from 'stores/companies';
import { putAll as putAllPermissionSets } from 'stores/permission-sets';
import { update as updateVisibilitySettings } from 'stores/visibility';
import { currentUserState } from 'utils/current-user-state';
import { isLocalhost } from 'utils/is-localhost';
import { getQueryAdvancedSettingsConfigKey } from './advanced-settings/advanced-settings';
import { oneDay } from './constants';

export const ACCOUNT_META_DATA_QUERY_KEY = ['account-meta-data'];
export const NOTIFICATION_DATA_QUERY_KEY = ['notification-data'];

const commonOptions: Pick<
  UseQueryOptions<AccountInitialData>,
  'queryKey' | 'queryFn' | 'staleTime' | 'refetchOnWindowFocus'
> = {
  queryKey: ACCOUNT_META_DATA_QUERY_KEY,
  queryFn: () => AccountAPI.getInitData(),
  staleTime: oneDay,
  refetchOnWindowFocus: false,
};

// This is a custom hook that will return the notification data via set timeout
// Based on this initial data we can update the pending notifications count in query client cache via websocket
export const useNotificationDataQuery =
  (): UseQueryResult<NotificationData> => {
    return useQuery({
      queryKey: NOTIFICATION_DATA_QUERY_KEY,
      // promise with timeout
      queryFn: () => {
        return new Promise((resolve) => {
          resolve({
            pendingNotifications: 0,
            newNotificationAvailable: false,
          });
        });
      },
    });
  };

export const useAccountDataQuery = (): UseQueryResult<AccountInitialData> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useQuery({
    ...commonOptions,
    onSuccess: ({
      account,
      accountMeta,
      accountConfig,
      userProfile,
      companies,
      permissionSetList,
      accountVisibilitySettings,
      personalTasks,
    }) => {
      if (accountConfig) {
        queryClient.setQueryData(
          getQueryAdvancedSettingsConfigKey(),
          accountConfig,
        );
      }
      dispatch(set(account));
      dispatch(success(userProfile));
      dispatch(putAllCompanies(companies ?? []));
      dispatch(putAllPermissionSets(permissionSetList));
      dispatch(personalTasksSlice.actions.fetchSuccess(personalTasks));
      if (accountVisibilitySettings) {
        dispatch(updateVisibilitySettings(accountVisibilitySettings));
      }
      currentUserState.userState = {
        currentUser: userProfile,
        vendorCompany: accountMeta.company,
      };

      // save in local storage for future use to avoid unnecessary api calls
      // since accountMeta is not going to change frequently
      // accountMeta will be cleared on logout
      // isLocalhost check id for dev purpose. Otherwise domain name has to be changed from inside the localstorage
      if (!isLocalhost()) {
        localStorage.setItem('accountMeta', JSON.stringify(accountMeta));
      }
    },
  });
};

export const useAllCompanies = (): UseQueryResult<Company[], unknown> => {
  return useQuery<AccountInitialData, unknown, Company[]>({
    ...commonOptions,
    select: (data) => data.companies,
  });
};

export const useAccountSignUpMutation = (): UseMutationResult<
  AccountSignUpResponse,
  unknown,
  AccountSignUpPayload
> => {
  return useMutation({
    mutationFn: (payload) => AccountAPI.signUp(payload),
    onSuccess: (res) => {
      if (res.templateId) {
        window.location.replace(
          `https://${
            res.account.primaryDomainName + RoutePaths.loginWithSession
          }?token=${res.session.sessionToken}&templateId=${res.templateId}`,
        );

        return;
      }
      window.location.replace(
        `https://${
          res.account.primaryDomainName + RoutePaths.loginWithSession
        }?token=${res.session.sessionToken}`,
      );
    },
  });
};

export const useIsDomainExistsMutation = (): UseMutationResult<
  IsDomainExistsResponse,
  unknown,
  IsDomainExistsPayload
> => {
  return useMutation({
    mutationFn: (payload) => AccountAPI.isDomainExists(payload),
  });
};

export const ACCOUNT_PROFILE_KEY = 'ACC_PROFILE';

export const useAccountProfile = (
  queryOptions: UseQueryOptions<
    Account,
    unknown,
    Account,
    typeof ACCOUNT_PROFILE_KEY
  > = {},
): UseQueryResult<Account> =>
  useQuery(ACCOUNT_PROFILE_KEY, () => AccountAPI.getAccountDetails(), {
    staleTime: GENERAL_API_STALE,
    ...queryOptions,
  });

const updateAccTimezoneFn = (data: UpdateAccountTimezonePayload) => {
  return AccountAPI.updateTimezone(data);
};

export const useAccountTimezoneMutation = (
  options: UseMutationOptions<
    Account,
    unknown,
    UpdateAccountTimezonePayload
  > = {},
): UseMutationResult<Account, unknown, UpdateAccountTimezonePayload> => {
  const queryClient = useQueryClient();

  return useMutation((data) => updateAccTimezoneFn(data), {
    ...options,

    onMutate: async () => {
      await queryClient.cancelQueries(ACCOUNT_PROFILE_KEY);

      const previous = queryClient.getQueryData<Account>(ACCOUNT_PROFILE_KEY);

      return { previous };
    },

    onSuccess: async (data, request, context) => {
      const prev = context?.previous;

      if (!prev) {
        return;
      }

      queryClient.setQueryData<Account>(ACCOUNT_PROFILE_KEY, {
        ...prev,
        timeZone: data.timeZone,
      });
    },
  });
};

export const CURRENCY_LIST_KEY = 'CURRENCY_LIST';

export const useCurrencyListQuery = (
  queryOptions: UseQueryOptions<
    CurrencyList,
    unknown,
    CurrencyList,
    typeof CURRENCY_LIST_KEY
  > = {},
): UseQueryResult<CurrencyList> =>
  useQuery(CURRENCY_LIST_KEY, () => AccountAPI.getCurrencyList(), {
    ...queryOptions,
    staleTime: Infinity,
  });

export const useAccountCurrencyMutation = (
  options: UseMutationOptions<
    Account,
    unknown,
    UpdateAccountCurrencyPayload
  > = {},
): UseMutationResult<Account, unknown, UpdateAccountCurrencyPayload> => {
  const queryClient = useQueryClient();

  return useMutation((data) => AccountAPI.updateCurrency(data), {
    ...options,

    onMutate: async () => {
      await queryClient.cancelQueries(ACCOUNT_PROFILE_KEY);

      const previous = queryClient.getQueryData<Account>(ACCOUNT_PROFILE_KEY);

      return { previous };
    },

    onSuccess: async (data, request, context) => {
      const prev = context?.previous;

      if (!prev) {
        return;
      }

      queryClient.setQueryData<Account>(ACCOUNT_PROFILE_KEY, {
        ...prev,
        currency: data.currency,
      });

      const previousInit = queryClient.getQueryData<AccountInitialData>(
        ACCOUNT_META_DATA_QUERY_KEY,
      );

      if (previousInit == null) {
        return;
      }

      queryClient.setQueryData<AccountInitialData>(
        ACCOUNT_META_DATA_QUERY_KEY,
        dotPropImmutable.set(previousInit, 'account.currency', data.currency),
      );
    },
  });
};
